import { Injectable } from '@angular/core';
import {
  ProductListConfigurationKeys,
  ProductListConfigurations,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import {
  mapListGroupingTypeToSearchSort,
  ProductListApiService,
  ProductListProductsApiService,
} from '@epicuro-next/data/engagement/engagement-api';
import {
  EntityType,
  ProductListGroupingType,
} from '@epicuro-next/data/engagement/engagement-models';
import { IProductListPatchModel } from '@epicuro-next/data/lists';
import { ProductService } from '@epicuro-next/data/product';
import { ListBasketInsightSort } from '@epicuro-next/data/reporting-api';
import { isCommercialList } from '@epicuro-next/features/engagement/list-utils';
import { List, Media, MediaType } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  ConfigurationsService,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import {
  loadListAnalytics,
  loadListsReports,
  selectBasketInsightsDefaultSorting,
  selectCanSeeAnalyticsRoles,
} from '@epicuro-next/state/analytics-entities';
import {
  enrichProductsWithPrices,
  selectMerchandisePricesState,
  setLoadedProductDiscounts,
} from '@epicuro-next/state/deprecated-merchandise-prices';
import {
  loadList,
  loadNextListProducts,
  removeProductFromListEntity,
  resetListProducts,
  selectListEntityFactory,
  selectListEntitySearchSortType,
  selectOpenedListIdFromUrl,
  setSearchSortTypeInListEntity,
  SORT_TYPE_PRODUCTS_UPDATE_STRATEGY,
  upsertListEntity,
} from '@epicuro-next/state/list-entities';
import { loadListProductReferences } from '@epicuro-next/state/list-product-reference-entities';
import { actions as feedActions } from '@epicuro-next/state/news-feed-state';
import { openOverlayOutlet, selectUrl } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { selectLookbookListIdInRoute } from '@epicuro-next/state/shop-the-look-state';
import {
  selectUserExternalId,
  selectUserRoles,
} from '@epicuro-next/state/user-state';
import { isNonNullish, isNullish, unique } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product, SearchSortType } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import { deleteProductsFromList } from '../../actions';
import { IEpicShowMetadata, IEpicShowProductMetadata } from '../../models';
import { OpenedListQueryParams } from '../../models/opened-list-query-params';
import {
  selectIsConsumingClonedEpicShow,
  selectOpenedList,
  selectOpenedListProductReferences,
  selectOpenedListProducts,
  selectOpenedListQueryParams,
  selectProductList,
} from '../../selectors';
import { productReferencesAvailabilityHandler } from '../../utils';

/**
 * Opened list effects
 */
@Injectable()
export class OpenedListEffects {
  constructor(
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private productListProductsService: ProductListProductsApiService,
    private productService: ProductService,
    private loggerService: LoggerService,
    private store: Store,
    private configService: ConfigurationsService,
  ) {}

  public canSeeAnalyticsRoles$ = this.store.pipe(
    select(selectCanSeeAnalyticsRoles),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public basketInsightsDefaultSorting$ = this.store.pipe(
    select(selectBasketInsightsDefaultSorting),
    filter(isNonNullish),
    take(1),
  );

  public siteId$ = this.store.pipe(
    select(selectSiteId),
    filter(isNonNullish),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  public accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private categoryField$ = this.configService.getConfigValue({
    featureName: 'product-category-field',
    defaultValue: 'category',
    namespace: 'config-strip-state',
  });

  public setOpenedList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedList),
      concatLatestFrom((action) => [
        this.store.pipe(select(selectProductList(action.id)), take(1)),
        this.store.pipe(
          select(selectListEntityFactory(action.id)),
          take(1),
          map((e) => e?.list),
        ),
        this.accountId$,
        this.siteId$,
      ]),
      switchMap(
        ([action, listFromMyLists, listFromEntities, accountId, siteId]) => {
          const list = listFromMyLists ?? listFromEntities;
          if (list && action.id) {
            return of(actions.setOpenedListSuccess({ list }));
          }

          return this.productListApi
            .get({ productListId: action.id, accountId, siteId })
            .pipe(
              map((item: List) =>
                item
                  ? actions.setOpenedListSuccess({ list: item })
                  : actions.setOpenedListError(),
              ),
              catchError(() => of(actions.setOpenedListError())),
            );
        },
      ),
    ),
  );

  public loadCommercialListProductReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      filter(({ list }) => isCommercialList(list)),
      map(({ list: { id } }) =>
        loadListProductReferences({
          productListId: id,
        }),
      ),
    ),
  );

  public reloadCommercialListProductReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateCommercialListSuccess),
      filter(({ list }) => isCommercialList(list)),
      concatLatestFrom(() => [
        this.store.pipe(
          select(
            selectConfigurationValueFrom<
              ProductListConfigurationKeys.AllowEditOfCommercialListProducts,
              ProductListConfigurations
            >(
              PRODUCT_LIST_NAMESPACE,
              ProductListConfigurationKeys.AllowEditOfCommercialListProducts,
              false,
            ),
          ),
        ),
      ]),
      filter(
        ([, allowEditOfCommercialListProducts]) =>
          !!allowEditOfCommercialListProducts,
      ),
      concatMap(
        ([
          {
            list: { id },
          },
        ]) => [
          resetListProducts({ listId: id }),
          loadListProductReferences({
            productListId: id,
          }),
        ],
      ),
    ),
  );

  /**
   * Effect to delete a product from opened list
   */
  public deleteProductFromOpenedList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductFromOpenedList),
      withLatestFrom(this.store.pipe(select(selectOpenedList))),
      filter(([_, list]) => Boolean(list)),
      concatMap(([action, list]) => [
        actions.deleteProductsFromList({
          list: list as List,
          products: [action.product],
        }),
        actions.deleteProductsFromOpenedList({
          products: [action.product],
        }),
      ]),
    ),
  );

  public removeProductFromOpenedListEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProductsFromList),
      withLatestFrom(this.store.pipe(select(selectOpenedListIdFromUrl))),
      filter(
        ([
          {
            list: { id: listId },
          },
          openedListId,
        ]) => listId === openedListId,
      ),
      map(
        ([
          {
            list: { id: listId },
            products: [{ productId }],
          },
        ]) => {
          return removeProductFromListEntity({ listId, productId });
        },
      ),
    ),
  );

  /**
   * Effect to delete a product from the opened list entity store
   */
  public deleteProductsFromOpenedListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchProductsBulkSuccess),
      withLatestFrom(this.store.pipe(select(selectOpenedList))),
      filter(([_, list]) => Boolean(list)),
      map(([action, list]) => this.mapBulkProducts(action.products, list)),
      filter((products) => Boolean(products?.itemsToDelete.length)),
      map((products) =>
        actions.deleteProductFromOpenedListSuccess({
          products: products?.itemsToDelete ?? [],
        }),
      ),
    ),
  );

  public updateMetadataOnDeleteProductsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductFromOpenedListSuccess),
      filter((action) => Boolean(action.products.length)),
      withLatestFrom(
        this.store.pipe(select(selectOpenedList)),
        this.store.pipe(select(selectIsConsumingClonedEpicShow)),
      ),
      // Check if epic show
      filter(([_, list]) =>
        Boolean(
          list &&
            list.mainMedia?.mediaType === MediaType.Directshow &&
            list.mainMedia.metadata,
        ),
      ),
      filter(([, , isConsumingClonedEpicShow]) => {
        // if consuming cloned epic show, don't update metada
        return !isConsumingClonedEpicShow;
      }),
      switchMap(([action, list]) => {
        try {
          const metadata: IEpicShowMetadata = JSON.parse(
            list?.mainMedia?.metadata ?? '',
          );

          const newProductsMeta = Object.keys(metadata.products ?? {})
            .filter(
              (id) =>
                !action.products.some((product) => product.productId === id),
            )
            .reduce(
              (
                acc: { [productId: string]: IEpicShowProductMetadata[] },
                key: string,
              ) => {
                acc[key] = metadata.products[key];

                return acc;
              },
              {},
            );

          return of(
            actions.createEpicShow({
              media: {
                ...list?.mainMedia,
                metadata: JSON.stringify({
                  groups: metadata.groups,
                  products: newProductsMeta,
                }),
              } as Media,
            }),
          );
        } catch {
          return EMPTY;
        }
      }),
    ),
  );

  public addProductsToOpenedListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchProductsBulkSuccess),
      withLatestFrom(this.store.pipe(select(selectOpenedList))),
      filter(([_, list]) => Boolean(list)),
      map(([action, list]) => this.mapBulkProducts(action.products, list)),
      filter((products) => Boolean(products?.itemsToAdd.length)),
      map((products) =>
        actions.addProductToOpenedListSuccess({
          products: products?.itemsToAdd ?? [],
        }),
      ),
    ),
  );

  public loadOpenedListProductsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addProductToOpenedListSuccess),
      withLatestFrom(
        this.store.pipe(select(selectOpenedListQueryParams)),
        this.store.pipe(select(selectOpenedListProducts)),
      ),
      filter(([, params, loadedProducts]) => {
        const loadedCount = loadedProducts.length;
        const expectedLoaded = params.page * params.size;

        return loadedCount <= expectedLoaded;
      }),
      map(() => actions.loadOpenedListProducts({ samePage: true })),
    ),
  );

  public updateOpenedListEntityAfterAddSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addProductToOpenedListSuccess),
      withLatestFrom(this.store.pipe(select(selectOpenedListQueryParams))),
      switchMap(([, params]) => {
        return [
          loadList({ listId: params.id as string }),
          resetListProducts({ listId: params.id as string }),
        ];
      }),
    ),
  );

  public updateListEntityIfListIsNotOpenedAfterPatchProductsBulkSuccess$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(actions.patchProductsBulkSuccess),
        withLatestFrom(this.store.pipe(select(selectOpenedListIdFromUrl))),
        switchMap(([action, openedListId]) => {
          return action.products
            .map(({ listId }) => listId)
            .filter(unique)
            .filter((id) => id !== openedListId)
            .flatMap((listId) => {
              return [resetListProducts({ listId }), loadList({ listId })];
            });
        }),
      ),
    );

  public loadEpicShowProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      map(({ list }) => list.mainMedia?.metadata),
      filter((metadata) => Boolean(metadata)),
      map((metadata) => {
        try {
          const parsedMetadata: IEpicShowMetadata = JSON.parse(
            metadata as string,
          );

          return Object.keys(parsedMetadata.products);
        } catch {
          return {};
        }
      }),
      map((ids: string[]) => actions.loadEpicShowProducts({ ids })),
    ),
  );

  public upsertEpicShowProductsReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadEpicShowProducts),
      concatLatestFrom(() => this.siteId$),
      map(([{ ids }, brandId]) =>
        ids.map((productId) => ({ productId, brandId: brandId.toLowerCase() })),
      ),
      map((references) =>
        actions.upsertEpicShowProductsReferences({ references }),
      ),
    ),
  );

  public upsertListEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      map((action) => action.list),
      filter(isNonNullish),
      concatLatestFrom((list) =>
        this.store.pipe(
          select(selectListEntityFactory(list.id)),
          map((e) => e?.list),
        ),
      ),
      filter(([_, listEntity]) => isNullish(listEntity)),
      map(([list]) =>
        upsertListEntity({
          listEntity: {
            listId: list.id,
            list,
            loading: false,
            loadingProducts: false,
            error: undefined,
            reorderingCount: 0,
            sortType: mapListGroupingTypeToSearchSort(
              list.groupingType as ProductListGroupingType,
            ),
          },
        }),
      ),
    ),
  );

  public loadOpenedListProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadOpenedListProducts),
      switchMap((action) =>
        this.getOpenedListData().pipe(map((data) => ({ ...data, action }))),
      ),
      concatLatestFrom(() => [this.siteId$, this.accountId$]),
      switchMap(
        ([{ action, listParams, sortField, list }, siteId, accountId]) => {
          // I don't know why this fallback is needed
          const listId = list?.id ?? listParams.id;
          if (!listId) return of(actions.loadOpenedListProductsError());
          else if (list && isCommercialList(list))
            return of(loadNextListProducts({ listId }));
          else {
            if (
              list?.originalProductList &&
              list.mainMedia?.mediaType === MediaType.Directshow
            )
              return this.loadEpicProductsAndStandardProducts(
                listParams,
                list,
                action.samePage,
                sortField,
                siteId,
                accountId,
              );
            else
              return this.loadStandardProducts(
                listParams,
                action.samePage,
                siteId,
                accountId,
                sortField,
              );
          }
        },
      ),
    ),
  );

  public updateOpenedListProductsWithDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLoadedProductDiscounts),
      withLatestFrom(
        this.store.select(selectMerchandisePricesState),
        this.store.select(selectOpenedListProducts),
      ),
      filter(([, , products]) => products.length > 0),
      map(([, merchandisePrices, products]) =>
        actions.updateOpenedListProductsWithDiscounts({
          products: enrichProductsWithPrices(products, merchandisePrices),
        }),
      ),
    ),
  );

  public setOpenedListError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.setOpenedListError),
        tap(() => this.logError('Failed to open a product list')),
      ),
    { dispatch: false },
  );

  public loadOpenedListProductsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.loadOpenedListProductsError),
        tap(() => this.logError('Failed to load products of the opened list')),
      ),
    { dispatch: false },
  );

  public setSpotlightActiveReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      withLatestFrom(this.store.pipe(select(selectUrl))),
      filter(([, url]) => url.includes(APP_PRIVATE_PATHS.productList.path)),
      map(([action]) => {
        const reference = {
          id: action.list.id,
          type: EntityType.ProductList,
        };

        return feedActions.setActiveReference({ reference });
      }),
    ),
  );

  public loadOpenedListAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      concatMap((action) =>
        of(action).pipe(
          concatLatestFrom(() => [
            this.store.pipe(select(selectUserExternalId)),
            this.store.pipe(select(selectUserRoles)),
            this.canSeeAnalyticsRoles$,
          ]),
          filter(
            ([
              { list },
              currentUserExternalId,
              currentUserRoles,
              canSeeAnalyticsRoles,
            ]) =>
              (list.creation?.user?.externalId ?? '') ===
                currentUserExternalId ||
              currentUserRoles.some((role) =>
                canSeeAnalyticsRoles.includes(role),
              ),
          ),
          map(([{ list }]) =>
            loadListsReports({
              listIds: [list.id],
            }),
          ),
        ),
      ),
    ),
  );

  public clearSpotlightActiveReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.clearOpenedList),
      map(() => {
        return feedActions.setActiveReference({ reference: null });
      }),
    ),
  );

  public setGroupingOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSearchSortTypeInListEntity),
      filter(
        ({ strategy }) =>
          strategy === SORT_TYPE_PRODUCTS_UPDATE_STRATEGY.OPENED_LIST,
      ),
      concatMap(() => [
        actions.resetOpenedListProducts(),
        actions.loadOpenedListProducts({ samePage: false }),
      ]),
    ),
  );

  public reorderProductInOpenedList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.reorderProductInOpenedList),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedList))),
      switchMap(([action, list]) =>
        this.productListApi
          .reorderProduct({
            productListId: list?.id as string,
            productId: action.productId,
            brandId: action.brandId,
            order: action.order,
          })
          .pipe(
            map(() =>
              actions.reorderProductInOpenedListSuccess({
                listId: list?.id as string,
                productId: action.productId,
                order: action.order,
              }),
            ),
          ),
      ),
    ),
  );

  public shopTheList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.shopTheList),
      concatLatestFrom(() => [
        this.store.pipe(select(selectOpenedListQueryParams)),
        this.store.pipe(select(selectListEntitySearchSortType())),
        this.store.pipe(select(selectLookbookListIdInRoute)),
        this.store.pipe(select(selectOpenedListProductReferences)),
        this.categoryField$,
        this.siteId$,
        this.accountId$,
      ]),
      switchMap(
        ([
          _,
          params,
          sortField,
          lookbookCurrentListId,
          productReferences,
          categoryField,
          siteId,
          accountId,
        ]) => {
          const productListId = lookbookCurrentListId ?? params.id ?? null;
          return !productListId
            ? [actions.loadOpenedListProductsError()]
            : this.productListProductsService
                .getProducts({
                  query: {
                    start: 0,
                    num: 100,
                    available: params.available,
                    productListId,
                    sortField,
                    siteId,
                    accountId,
                    productReferences,
                  },
                })
                .pipe(
                  switchMap(({ products }) => {
                    const sortedProducts = this.groupSortProducts(
                      products as Product[],
                      categoryField as string,
                    );
                    const product = sortedProducts[0];

                    const { brandId, productId } = product;
                    const ids: string[] = [
                      product.parent?.productId,
                      productId,
                      productId,
                    ]
                      .filter(isNonNullish)
                      .slice(0, 2);
                    const overlayPath = ['cs', brandId, ...ids];
                    return [
                      openOverlayOutlet({ overlayPath }),
                      actions.setFullList({ list: sortedProducts }),
                    ];
                  }),
                  catchError(() => of(actions.shopTheListError())),
                );
        },
      ),
    ),
  );

  public loadListAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setOpenedListSuccess),
      switchMap(({ list }) =>
        this.basketInsightsDefaultSorting$.pipe(
          map((defaultSorting) =>
            loadListAnalytics({
              listId: list.id,
              sortBy: defaultSorting as ListBasketInsightSort,
            }),
          ),
        ),
      ),
    ),
  );

  public updateEpicShowAllProductsAvailabilityOnUserNoRole = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateEpicShowProductsAvailability),
      concatLatestFrom(() => [this.siteId$, this.accountId$]),
      switchMap(([{ products }, siteId, accountId]) => {
        const query = {
          siteId,
          productReferences: products.map(({ productId }) => ({
            brandId: siteId?.toLowerCase() ?? '',
            productId,
          })),
        };

        return this.productService
          .checkProductsAvailabilityByReferences(accountId, query)
          .pipe(
            map(
              ({ availableProductReferences, unavailableProductReferences }) =>
                productReferencesAvailabilityHandler(
                  availableProductReferences,
                  unavailableProductReferences,
                  products,
                ),
            ),
          );
      }),
      map((products) =>
        actions.loadEpicShowProductsSuccess({
          products: products as Product[],
        }),
      ),
    ),
  );

  private getOpenedListData() {
    return combineLatest([
      this.store.pipe(select(selectOpenedListQueryParams)),
      this.store.pipe(select(selectListEntitySearchSortType())),
      this.store.pipe(select(selectOpenedList), filter(isNonNullish), take(1)),
    ]).pipe(
      take(1),
      map(([listParams, sortField, list]) => ({
        listParams,
        sortField,
        list,
      })),
    );
  }

  private groupSortProducts(
    items: Product[],
    categoryField: string,
  ): Product[] {
    const grouped = items.reduce(
      (acc: { [key: string]: Product[] }, curr: Product) => {
        const category = curr.attributes?.[categoryField] as string;
        return {
          ...acc,
          [category]: [...(acc[category] || []), curr],
        };
      },
      {},
    );

    return Object.values(grouped)
      .sort((a: Product[], b: Product[]) => b.length - a.length)
      .flat();
  }

  private logError(message: string) {
    this.loggerService.exception({
      error: new Error(message),
    });
  }

  private mapBulkProducts(
    products: actions.PatchProducts[],
    list?: List | null,
  ) {
    if (!list?.id) {
      return;
    }

    const productsMap = new Map<string, IProductListPatchModel>(
      products.map(({ listId, patchModel }) => [listId, patchModel]),
    );

    return productsMap.get(list?.id);
  }

  private getProducts(
    params: OpenedListQueryParams,
    samePage: boolean,
    siteId: string,
    accountId: string,
    sortField?: SearchSortType | null,
  ) {
    const payload = {
      query: {
        productListId: params?.id,
        start: Math.max((params?.page - (samePage ? 1 : 0)) * params?.size, 0),
        num: params.size,
        sortField,
        available: params?.available,
        productReferences: params?.productReferences,
        accountId,
        siteId,
      },
      cacheFirst: false,
    };

    return this.productListProductsService
      .getProducts(payload)
      .pipe(withLatestFrom(this.store.select(selectMerchandisePricesState)));
  }

  private loadStandardProducts(
    listParams: OpenedListQueryParams,
    samePage: boolean,
    siteId: string,
    accountId: string,
    sortField?: SearchSortType | null,
  ) {
    return this.getProducts(
      listParams,
      samePage,
      siteId,
      accountId,
      sortField,
    ).pipe(
      map(([data, discounts]) =>
        actions.loadOpenedListProductsSuccess({
          products: enrichProductsWithPrices(
            data.products as Product[],
            discounts,
          ),
          samePage,
          total: data.resultCount,
        }),
      ),
      catchError(() => of(actions.loadOpenedListProductsError())),
    );
  }

  private loadEpicProductsAndStandardProducts(
    listParams: OpenedListQueryParams,
    list: List,
    samePage: boolean,
    sortField: SearchSortType | undefined | null,
    siteId: string,
    accountId: string,
  ) {
    const productRequests = this.getProductRequestsForEpicAndStandardLists(
      listParams,
      list,
      samePage,
      sortField,
      siteId,
      accountId,
    );

    return combineLatest(productRequests).pipe(
      switchMap(
        ([
          [currentProducts, currentProductsDiscounts],
          [availableEpicProducts, availableEpicProductsDiscounts],
        ]) => {
          const epicProducts = availableEpicProducts.products;

          const productsWithPrices = {
            products: enrichProductsWithPrices(
              epicProducts as Product[],
              availableEpicProductsDiscounts,
            ),
          };

          return [
            actions.loadEpicShowProductsSuccess(productsWithPrices),
            actions.loadOpenedListProductsSuccess({
              products: enrichProductsWithPrices(
                currentProducts.products as Product[],
                currentProductsDiscounts,
              ),
              samePage,
              total: currentProducts.resultCount,
            }),
          ];
        },
      ),
      catchError(() => of(actions.loadOpenedListProductsError())),
    );
  }

  private getProductRequestsForEpicAndStandardLists(
    listParams: OpenedListQueryParams,
    list: List,
    samePage: boolean,
    sortField: SearchSortType | undefined | null,
    siteId: string,
    accountId: string,
  ) {
    const modifiedParams = {
      ...listParams,
      id: list?.originalProductList?.id,
    };

    return [
      this.getProducts(listParams, samePage, siteId, accountId, sortField),
      this.getProducts(modifiedParams, samePage, siteId, accountId, sortField),
    ];
  }
}
